import React, { useState, useEffect, useContext } from "react";
import { getSimilarProducts } from "../services/apiService";
import "../styles/RelatedProducts.css";
import noimage from "../assets/noimage.webp";
import { useNavigate } from "react-router-dom";
import { FaRegHeart, FaHeart } from "react-icons/fa";
import { FaArrowDown } from "react-icons/fa6";

import {
  AddItemToWishList,
  AddItemToCart,
  DeleteFromWishList,
  getProductQty,
} from "../services/Cart";

import { showSnackbar } from "../comopnents/snackbar.jsx";
import AuthModal from "./AuthModal.jsx";
import { CartContext } from "../contextApi/CartContext.jsx";
import { withImageVersion } from "../contextApi/ImageVersionContext";
import LoadingSpinner from "./LoadingSpinner.jsx";
import CartQuantityControl from "./CartQuantityControl.jsx";
import PurchaseOptionsModal from "./PurchaseOptionsModal.jsx";

import {
  buildEnhancedPurchaseData,
  getMinOrderQty,
  getMinimumPurchaseQuantity,
  getPurchaseQuantityStep,
  getSavedCartPurchaseData,
  shouldShowPurchaseModal,
} from "../utils/purchaseOptions.js";

import { Notifyed } from "../services/Auth.jsx";
import { isOutOfStock } from "../utils/stock.js";

const RelatedProducts = ({ productId, onProductClick }) => {
  const [similarProducts, setSimilarProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  const [wishlist, setWishlist] = useState({});

  const [wishlistItems, setWishlistItems] = useState([]);

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pendingCartId, setPendingCartId] = useState(null);

  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [pendingPurchaseProduct, setPendingPurchaseProduct] = useState(null);

  const navigate = useNavigate();

  const { addToCart, itemList = [], setCartQty } = useContext(CartContext);

  const goToProductDetail = (productId) => {
    if (onProductClick) {
      onProductClick();
    }

    navigate(`/productdetail?productid=${productId}`);
  };

  const handleNotifyMe = async (productid) => {
    const userId = localStorage.getItem("userId");

    if (!userId) {
      setPendingCartId({
        productId: productid,
        qty: 1,
      });

      setShowAuthModal(true);
      return;
    }

    try {
      await Notifyed({
        customerid: userId,
        productid,
      });

      showSnackbar("success", "We will notify you when product is available");
    } catch (error) {
      console.error("Notify me error:", error.response?.data || error.message);

      showSnackbar("error", "Failed to register notify request");
    }
  };

  const openPurchaseModal = (product) => {
    const userId = localStorage.getItem("userId");

    if (!userId) {
      setPendingCartId({
        productId: product?.productid || product,
        qty: 1,
      });

      setShowAuthModal(true);
      return;
    }

    if (!shouldShowPurchaseModal(product)) {
      handleAddToCart(product, getMinOrderQty(product));

      return;
    }

    setPendingPurchaseProduct(product);
    setShowPurchaseModal(true);
  };

  const confirmPurchaseType = async (purchaseType, couponData) => {
    if (!pendingPurchaseProduct) return;

    const purchaseData = buildEnhancedPurchaseData(
      pendingPurchaseProduct,
      purchaseType,
      couponData,
    );

    await handleAddToCart(
      pendingPurchaseProduct,
      purchaseData.quantity,
      purchaseData,
    );

    setShowPurchaseModal(false);
    setPendingPurchaseProduct(null);
  };

  const handleAddToCart = async (product, qty = 1, purchaseData = null) => {
    const selectedProductId = product?.productid || product;

    if (isOutOfStock(product)) {
      await handleNotifyMe(selectedProductId);
      return;
    }

    const userId = localStorage.getItem("userId");

    if (!userId) {
      setPendingCartId({
        product,
        productId: selectedProductId,
        qty,
      });

      setShowAuthModal(true);
      return;
    }

    try {
      const payload = {
        customerid: userId,
        productid: selectedProductId,
        qty,
        stock: product?.stock,
      };

      await AddItemToCart(payload);

      addToCart(selectedProductId, qty, purchaseData);

      showSnackbar("success", "Item added to cart!");
    } catch (error) {
      console.error(
        "Add to cart error:",
        error.response?.data || error.message,
      );

      showSnackbar("error", "Failed to add item to cart!");
    }
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);

    if (pendingCartId) {
      const pendingProduct = pendingCartId.product || pendingCartId.productId;

      handleAddToCart(pendingProduct, pendingCartId.qty || 1);

      setPendingCartId(null);
    }
  };

  const handleCartQtyChange = async (productId, newQty) => {
    const userId = localStorage.getItem("userId");

    if (!userId) {
      setPendingCartId({
        productId,
        qty: newQty,
      });

      setShowAuthModal(true);
      return;
    }

    try {
      await getProductQty({
        user_id: userId,
        productid: productId,
        qty: newQty,
      });

      setCartQty(productId, newQty);
    } catch (error) {
      console.error("API Error:", error?.message || error);

      showSnackbar("error", "Failed to update quantity");
    }
  };

  const handleDeleteWishList = async (productid) => {
    try {
      const userId = localStorage.getItem("userId");

      if (!userId) return;

      await DeleteFromWishList({
        customerid: userId,
        productid,
      });

      setWishlist((prev) => ({
        ...prev,
        [productid]: false,
      }));

      setWishlistItems((prev) => prev.filter((id) => id !== productid));

      showSnackbar("success", "Removed from wishlist");
    } catch (error) {
      console.error("Error deleting from wishlist:", error);

      showSnackbar("error", "Failed to remove from wishlist");
    }
  };

  const AddToWishList = async (productid) => {
    const userId = localStorage.getItem("userId");

    if (!userId) {
      setPendingCartId({
        productId: productid,
        qty: 1,
      });

      setShowAuthModal(true);
      return;
    }

    if (wishlistItems.includes(productid)) {
      showSnackbar("info", "Item already in wishlist");

      return;
    }

    try {
      const response = await AddItemToWishList({
        productid,
        customerid: userId,
      });

      /*
       * FIX:
       * setWishlistedProducts was not defined.
       * Using setWishlist instead.
       */

      setWishlistItems((prev = []) => [...prev, productid]);

      setWishlist((prev = {}) => ({
        ...prev,
        [productid]: true,
      }));

      if (response?.status === true || response?.success === true) {
        showSnackbar("success", "Item added to Wishlist!");
      } else {
        showSnackbar("success", "Item added to Wishlist!");
      }
    } catch (error) {
      console.error("Add to wishlist error:", error);

      /*
       * Remove optimistic state
       * if API fails
       */

      setWishlist((prev = {}) => ({
        ...prev,
        [productid]: false,
      }));

      setWishlistItems((prev = []) => prev.filter((id) => id !== productid));

      showSnackbar("error", "Failed to add item to Wishlist!");
    }
  };

  useEffect(() => {
    if (productId) {
      loadSimilarProducts(productId);
    }
  }, [productId]);

  const loadSimilarProducts = async (id) => {
    try {
      setLoading(true);

      const res = await getSimilarProducts(id);

      setSimilarProducts(res?.data || []);
    } catch (error) {
      console.error("Error loading similar products:", error);

      setSimilarProducts([]);
    } finally {
      setLoading(false);
    }
  };

  if (!loading && similarProducts.length === 0) {
    return null;
  }

  return (
    <section className="bestseller">
      <div className="bestseller-header">
        <h2>RELATED PRODUCTS</h2>
      </div>

      {loading && <LoadingSpinner text="Loading related products..." />}

      {!loading && (
        <div className="shopproductgrid">
          {similarProducts.map((product) => {
            const qty =
              itemList.find(
                (item) => String(item.productId) === String(product.productid),
              )?.count || 0;

            const minorderqty =
              product.minorderqty || product.dtl?.[0]?.minorderqty || 1;

            const stock = Number(product.stock ?? product.dtl?.[0]?.stock ?? 0);

            /*
             * FIX FOR:
             * ReferenceError: netrate is not defined
             */

            const netrate = Number(
              product.netrate ?? product.dtl?.[0]?.netrate ?? 0,
            );

            /*
             * FIX FOR:
             * discount is not defined
             */

            const discount = Number(product.discount ?? 0);

            const savedPurchaseData = getSavedCartPurchaseData(
              product.productid,
            );

            const purchaseType = savedPurchaseData?.purchaseType || "piece";

            return (
              <div key={product.productid} className="product-card bestsellers-card">
                {/* Discount / Netrate Badge */}

                {((stock > 0 && netrate > 0) || discount > 0) && (
                  <span
                    className="discount-badge"
                    style={{
                      background: "linear-gradient(135deg, #ff4d4d, #d90429)",
                      color: "#fff",
                      padding: "4px 2px",
                      borderRadius: "6px",
                      fontSize: "11px",
                      fontWeight: "700",
                      lineHeight: 1,
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {stock > 0 && netrate > 0 ? "Netrate" : `${discount}% OFF`}
                  </span>
                )}

                {/* Wishlist and Cart */}

                <div className="related-actions">
                  {/* Wishlist */}

                  <button
                    className="related-action-btn"
                    onClick={() => {
                      const isCurrentlyWishlisted =
                        !!wishlist[product.productid];

                      if (isCurrentlyWishlisted) {
                        handleDeleteWishList(product.productid);
                      } else {
                        AddToWishList(product.productid);
                      }
                    }}
                  >
                    {wishlist[product.productid] ? (
                      <FaHeart color="red" />
                    ) : (
                      <FaRegHeart />
                    )}
                  </button>

                  {/* Cart */}

                </div>

                {/* Product Image */}

                <img
                  src={withImageVersion(product.image1?.trim() ? product.image1 : null) || noimage}
                  alt={product.modelno || "Product"}
                  className="related-product-img"
                  onError={(e) => {
                    e.target.src = noimage;
                  }}
                  onClick={() => goToProductDetail(product.productid)}
                />

                {/* Product Information */}

                <div className="related-product-info">
                  <h4
                    className="
                      bestsellers-category
                    "
                    onClick={() => goToProductDetail(product.productid)}
                    style={{
                      cursor: "pointer",
                    }}
                  >
                    {product.modelno}
                  </h4>

                  <p
                    className="
                      bestsellers-category
                    "
                  >
                    {product.category || "Category"}
                  </p>

                  <p
                    className="
                      bestsellers-category
                    "
                  >
                    {product.brand || "Brand"}
                  </p>

                  {/* Price */}

                  <div className="related-price-row">
                    {/* MRP */}

                    {product.mrp && netrate <= 0 && (
                      <div className="related-mrp">
                        <FaArrowDown color="black" />
                        MRP:
                        <span>₹{Number(product.mrp || 0).toFixed(2)}</span>
                      </div>
                    )}

                    {/* Netrate */}

                    {netrate > 0 ? (
                      <>
                        {/* MRP */}
                        {product.mrp && (
                          <div className="related-mrp">
                            <FaArrowDown color="black" />
                            MRP:
                            <span>₹ {Number(product.mrp).toFixed(2)}</span>
                          </div>
                        )}

                        {/* Net Rate */}
                        <div
                          className="related-sale-price"
                        >
                          ₹{" "} {netrate.toFixed(2)}
                        </div>
                      </>
                    ) : product.mrp && discount > 0 ? (
                      <div
                        className="related-sale-price"
                      >
                        ₹{" "}
                        {(
                          Number(product.mrp) -
                          (Number(product.mrp) * discount) / 100
                        ).toFixed(2)}
                      </div>
                    ) : null}
                  </div>

                  <div className="related-card-actions">
                    {stock <= 0 ? (
                      <button
                        type="button"
                        className="related-card-cart-button related-card-notify-button"
                        onClick={() => handleNotifyMe(product.productid)}
                      >
                        Notify Me
                      </button>
                    ) : qty > 0 ? (
                      <CartQuantityControl
                        qty={qty}
                        minQty={getMinimumPurchaseQuantity(product, purchaseType)}
                        step={
                          purchaseType !== "piece"
                            ? getPurchaseQuantityStep(product, purchaseType)
                            : undefined
                        }
                        stock={stock}
                        purchaseType={purchaseType}
                        product={product}
                        onChange={(newQty) =>
                          handleCartQtyChange(product.productid, newQty)
                        }
                        className="qty-box"
                      />
                    ) : (
                      <button
                        type="button"
                        className="related-card-cart-button related-card-add-button"
                        onClick={() => openPurchaseModal(product)}
                      >
                        Add to Cart
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Authentication Modal */}

      {showAuthModal && (
        <AuthModal
          initialMode="login"
          onClose={() => setShowAuthModal(false)}
          onSuccess={handleAuthSuccess}
        />
      )}

      {/* Purchase Options Modal */}

      <PurchaseOptionsModal
        isOpen={showPurchaseModal}
        product={pendingPurchaseProduct}
        onClose={() => {
          setShowPurchaseModal(false);
          setPendingPurchaseProduct(null);
        }}
        onConfirm={confirmPurchaseType}
      />
    </section>
  );
};

export default RelatedProducts;
